import { getAppId } from '@shared/composables/useAppContext';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ChildParent } from '@shared/types/person';
import { useOrganizationStore } from './organization';

export interface User {
    id: number
    fullName: string
    firstName: string
    lastName: string
    email?: string
    gender?: string
    hasProfileImage: boolean
    profileImageApiUrl: string
    street?: string
    zip?: string
    city?: string
    phone?: string
    phoneMobile?: string
    hasActiveContract?: boolean
    timeTrackingEnabled?: boolean
    canCreateNews?: boolean
    canCreateEvents?: boolean
    newsClearingsAllowedToManage?: string[]
    eventClearingsAllowedToManage?: string[]
    // Parent-app account profile fields (from GET /users): the user-type discriminators and the
    // per-subject permission map. `permissionsGrantedToLoggedInUser` is keyed by permission name
    // and holds booleans at runtime (Angular UserPermissionService.isGranted reads `[perm] === true`),
    // despite the legacy `string[]` typing in the Angular model.
    parent?: boolean
    child?: boolean
    employee?: boolean
    dateOfBirth?: string
    // Privacy visibility flags (parent edit profile, GET /users → PUT /users/:id/publish-settings):
    // master toggle + per-field visibility for the parent's own contact data and the children's birthdays.
    publishContactData?: boolean
    publishContactDataAddress?: boolean
    publishContactDataPhone?: boolean
    publishContactDataMobile?: boolean
    publishContactDataEmail?: boolean
    publishContactChildDateOfBirth?: boolean
    permissionsGrantedToLoggedInUser?: Record<string, boolean>
    parents?: ChildParent[]
    _cacheBuster?: number
}

export const ContractStatus = {
    ended: 0,
    running: 1,
    scheduled: 2,
    no_membership: 3,
} as const;
// eslint-disable-next-line ts/no-redeclare -- intentional: value + type with same name
export type ContractStatus = (typeof ContractStatus)[keyof typeof ContractStatus];

// Angular `logoutCallback`: runs while the session is still valid, may be async.
export type LogoutCallback = () => unknown | Promise<unknown>;

export interface LoginUserToken {
    token: string
    name: string
    institutionName: string
    institutionId: number
    federationId?: number
    contractStatus?: ContractStatus
    contractType?: 'regular' | 'passive' | 'guest'
}

// Keys are built inside the store factory (not at module level): appId only exists at
// runtime on window.kgrDynamicConfig (set in main.ts), and the factory first runs after that.
function storageKeys() {
    const appId = getAppId();
    // parent + club_member use the unprefixed auth-token key for legacy reasons (1:1 with
    // Angular authentication.service `getAuthTokenStorageKey`); all other apps suffix with appId.
    const legacyUnprefixed = appId === 'kindergarten_parent_app' || appId === 'club_member_app';
    const authSuffix = legacyUnprefixed ? '' : `_${appId}`;
    return {
        TOKEN_KEY: `kgr_authToken${authSuffix}`,
        TOKENS_KEY: `kgr_authTokens${authSuffix}`,
        INSTITUTION_KEY: `kgr_institutionId_${appId}`,
        MIGRATED_KEY: `kgr_legacyAuthMigrated_${appId}`,
    };
}

// Numeric JWT claim, or null for an absent/undecodable token. The claims we read mirror Angular's
// `Credentials`: `userId` = main user (accounts with several profiles — parent app: parent +
// children from `/users` — where the main user is not necessarily `users[0]`), `institutionId`,
// plus the standard `exp`.
function decodeJwtNumber(token: string | null, claim: 'userId' | 'exp' | 'institutionId'): number | null {
    if (!token) {
        return null;
    }
    try {
        const part = token.split('.')[1];
        const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload[claim] === 'number' ? payload[claim] : null;
    } catch {
        return null;
    }
}

const decodeJwtUserId = (token: string | null) => decodeJwtNumber(token, 'userId');
const decodeJwtExp = (token: string | null) => decodeJwtNumber(token, 'exp');
const decodeJwtInstitutionId = (token: string | null) => decodeJwtNumber(token, 'institutionId');

// The Angular app persisted the session through @capacitor/preferences (native UserDefaults /
// SharedPreferences, `CapacitorStorage.`-prefixed localStorage on web) while we read plain
// localStorage — under the very same key names, which is what makes this silent. A device that
// updates from the Angular build to this one therefore finds nothing and looks logged out, even
// though the session is right there in the other store. Copy it over once.
//
// Angular never persisted the institution id (it read it off the JWT `institutionId` claim), so
// derive it here too — without it `initApp()` bails on `!institutionId` and the app would sit
// logged-in but uninitialised.
//
// Must run BEFORE `app.use(router)`: installing the router starts the initial navigation, and its
// guard redirects to /home/login on `!isSessionValid`. See each app's main.ts.
/**
 * Ob diese App die Legacy-Migration ueberhaupt installiert. Nur die vier nativ gewrappten Apps rufen
 * `migrateLegacyAuthStorage()` in ihrer `main.ts`; club-offering und club-member-registration sind
 * reine Browser-SPAs ohne nativen Vorgaenger und tun es bewusst nicht. `logout()` schrieb den
 * Einmal-Marker trotzdem — dort ist er reines Rauschen im localStorage, das beim Debuggen die Frage
 * aufwirft, warum eine App ohne Migration einen Migrations-Marker fuehrt (gemeldet 17.09.2026).
 */
let legacyMigrationInstalled = false;

export async function migrateLegacyAuthStorage(): Promise<void> {
    legacyMigrationInstalled = true;
    const { TOKEN_KEY, TOKENS_KEY, INSTITUTION_KEY, MIGRATED_KEY } = storageKeys();
    // Runs at most once per install. Without the marker every cold start would re-run the import,
    // and a logout — which can only clear OUR store — would be undone on the next app launch: the
    // legacy session is still sitting in Preferences, so the user comes back logged in. `logout()`
    // deletes the legacy copy as well, but the marker is what makes the one-shot semantics explicit
    // (and covers a Preferences.remove that failed on a locked device).
    if (localStorage.getItem(MIGRATED_KEY) !== null) {
        return;
    }

    try {
        // Anything already in our own store wins — this only ever fills a first-run blank.
        if (localStorage.getItem(TOKEN_KEY) === null) {
            const { Preferences } = await import('@capacitor/preferences');
            const legacyToken = (await Preferences.get({ key: TOKEN_KEY })).value;
            // Angular only ever put a token into its credentials store after `validateAuthToken`
            // (decode + `exp` check), so an expired one must not be imported either: `isLoggedIn`
            // is mere token presence, and the login view would bounce such a session to a guarded
            // route, where the guard answers with the re-auth password overlay instead of the login
            // screen. A guard rather than a live path — production issues app tokens with a ~10y
            // TTL (`jwt_token_ttl`), so sessions do not expire in the field.
            const legacyExp = decodeJwtExp(legacyToken);
            const legacyTokenIsValid = legacyExp !== null && legacyExp > Date.now() / 1000;

            if (legacyToken !== null && legacyTokenIsValid) {
                localStorage.setItem(TOKEN_KEY, legacyToken);

                const institutionId = decodeJwtInstitutionId(legacyToken);
                if (institutionId !== null) {
                    localStorage.setItem(INSTITUTION_KEY, String(institutionId));
                }

                // Multi-account list (account switcher). Optional — a missing or unparseable list
                // just means the user starts with the single migrated session.
                const legacyTokens = (await Preferences.get({ key: TOKENS_KEY })).value;
                if (legacyTokens !== null) {
                    localStorage.setItem(TOKENS_KEY, legacyTokens);
                }
            }
        }

        localStorage.setItem(MIGRATED_KEY, 'true');
    } catch (e) {
        // Deliberately not marked as done — a failed import is retried on the next launch.
        console.error('[user] legacy auth storage migration failed', e);
    }
}

// Counterpart to the import above: drop the Angular-era session from @capacitor/preferences so a
// logout is final. Runs on every logout, not only right after a migration — the legacy copy stays
// readable for the lifetime of the install, and a device that skipped the import (because it was
// already carrying a Vue session) still has it.
async function clearLegacyAuthStorage(tokenKey: string, tokensKey: string): Promise<void> {
    try {
        const { Preferences } = await import('@capacitor/preferences');
        await Promise.all([Preferences.remove({ key: tokenKey }), Preferences.remove({ key: tokensKey })]);
    } catch (e) {
        console.warn('[user] clearing legacy auth storage failed', e);
    }
}

export const useUserStore = defineStore('user', () => {
    const { TOKEN_KEY, TOKENS_KEY, INSTITUTION_KEY, MIGRATED_KEY } = storageKeys();

    function readLoginUserTokensFromStorage(): LoginUserToken[] {
        try {
            return JSON.parse(localStorage.getItem(TOKENS_KEY) ?? '[]') ?? [];
        } catch {
            return [];
        }
    }

    // Third-party context (club-offering runs in an iFrame): reading localStorage can THROW when
    // site data is blocked or partitioned, and an exception here would abort store creation — the
    // app would never mount. Degrade to "nothing stored" instead.
    function readStored(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    const token = ref<string | null>(readStored(TOKEN_KEY));
    const institutionId = ref<number | null>(Number(readStored(INSTITUTION_KEY)) || null);
    const currentUser = ref<User | null>(null);
    // All profiles of the active account (parent app: parent + children from `GET /users`);
    // single-user apps (employee) hold exactly one entry.
    const familyUsers = ref<User[]>([]);
    const loginUserTokens = ref<LoginUserToken[]>(readLoginUserTokensFromStorage());

    const isLoggedIn = computed(() => !!token.value);
    // Session validity (Angular UserCredentialsStore.isDecodedTokenValid): used by the router guard
    // to decide between letting a navigation through and triggering the re-auth overlay. Only treats
    // the session as expired when the JWT `exp` claim is present AND in the past; a missing/undecodable
    // exp falls back to token presence (presence is the safe default for the migration).
    const isSessionValid = computed(() => {
        if (!token.value) {
            return false;
        }
        const exp = decodeJwtExp(token.value);
        return exp === null || exp > Date.now() / 1000;
    });
    const mainUserId = computed(() => decodeJwtUserId(token.value));
    // Angular `*kgrIfActiveContract`: users().some(u => u.hasActiveContract) — über ALLE geladenen
    // Familienmitglieder (Eltern-App: Elternteil + Kinder), nicht nur den Hauptnutzer.
    const hasAtLeastOneActiveContract = computed(() => familyUsers.value.some((u) => u.hasActiveContract === true));
    const hasMultipleAccounts = computed(() => loginUserTokens.value.length > 1);
    // Every account stored on this device, by JWT `userId`. Angular's notification-preferences
    // service derives the push `userIds` exactly this way (decode each stored login token) — that
    // is what the plural `update-device-token-for-users` endpoint is for: one device token has to
    // reach every account on the device, not just the active one. Reading it off the stored JWTs
    // also means push registration never has to wait for `GET /users`.
    // The active session is folded in as a fallback: a session restored from the legacy Angular
    // store may carry the token without the account list.
    const deviceAccountUserIds = computed(() => {
        const ids = loginUserTokens.value
            .map((loginUserToken) => decodeJwtUserId(loginUserToken.token))
            .filter((id): id is number => id !== null);
        if (mainUserId.value !== null && !ids.includes(mainUserId.value)) {
            ids.push(mainUserId.value);
        }
        return ids;
    });

    /**
     * Angular's `AuthenticationService.loginByToken(token, persist = true)` — same signature, same
     * default. A real member login persists; the anonymous guest token of club-offering does NOT
     * (`GuestAuthenticationService` writes straight into the in-memory `UserCredentialsStore`,
     * bypassing the persisting path). Without the flag the guest session would outlive the tab,
     * which Angular never does. See APP_MIGRATE.md §3.
     */
    function setToken(jwt: string, persist = true) {
        token.value = jwt;
        if (!persist) {
            return;
        }
        try {
            localStorage.setItem(TOKEN_KEY, jwt);
        } catch (error) {
            console.error(error);
        }
    }

    function setInstitutionId(id: number, persist = true) {
        institutionId.value = id;
        if (!persist) {
            return;
        }
        localStorage.setItem(INSTITUTION_KEY, String(id));
    }

    function setUser(user: User) {
        currentUser.value = user;
    }

    function setFamilyUsers(users: User[]) {
        familyUsers.value = users;
    }

    function setLoginUserTokens(tokens: LoginUserToken[]) {
        loginUserTokens.value = tokens;
        localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    }

    // 1:1 Angular `AuthenticationService.loginByToken()`: der Token wird VOR dem Wechsel validiert
    // (`UserCredentialsStore.validateAuthToken` → Decode + `exp`-Pruefung). Schlaegt das fehl, wird
    // ausgeloggt und `false` geliefert — die Aufrufer duerfen dann NICHT navigieren.
    // Bewusst strenger als `isSessionValid`: dort gilt ein fehlendes `exp` als gueltig (Migrations-
    // Default fuer den laufenden Login), hier gilt Angulars `isDecodedTokenValid`, das ohne `exp`
    // false liefert — ein gespeicherter Token ohne Ablauf ist nicht vertrauenswuerdig.
    function loginByToken(loginUserToken: LoginUserToken): boolean {
        const exp = decodeJwtExp(loginUserToken.token);
        if (exp === null || exp <= Date.now() / 1000) {
            console.warn('[user] invalid token - logged out');
            void logout();
            return false;
        }
        setToken(loginUserToken.token);
        setInstitutionId(loginUserToken.institutionId);
        currentUser.value = null;
        return true;
    }

    // Angular `AuthenticationService.addLogoutCallback()`: work that has to happen while the session
    // is still authenticated (push device-token DELETE) hangs off the logout itself, not off the one
    // logout screen — the app menus call `logout()` directly and would otherwise skip it.
    let onLogoutCallbacks: LogoutCallback[] = [];

    function addLogoutCallback(callback: LogoutCallback) {
        onLogoutCallbacks.push(callback);
    }

    async function logout(): Promise<void> {
        const callbacks = onLogoutCallbacks;
        onLogoutCallbacks = [];
        // Every callback runs to completion BEFORE the auth token is dropped, and a failing one must
        // not block the logout (Angular catches per callback and proceeds).
        await Promise.all(
            callbacks.map(async (callback) => {
                try {
                    await callback();
                } catch (e) {
                    console.warn('[user] logout callback failed, proceeding with logout', e);
                }
            }),
        );

        token.value = null;
        institutionId.value = null;
        currentUser.value = null;
        familyUsers.value = [];
        loginUserTokens.value = [];
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKENS_KEY);
        localStorage.removeItem(INSTITUTION_KEY);
        // The migration is a one-shot, but keep the marker in place explicitly: dropping it here
        // would re-arm the import and resurrect the session on the next cold start. Only meaningful
        // where the migration is actually installed — see `legacyMigrationInstalled`.
        if (legacyMigrationInstalled) {
            localStorage.setItem(MIGRATED_KEY, 'true');
        }
        await clearLegacyAuthStorage(TOKEN_KEY, TOKENS_KEY);

        // The organization store is a Pinia singleton and survives the logout, so without this the
        // public views keep rendering the institution the user just left: contact/imprint and the
        // login start image. Reset to the pre-login state — the white-label client's own org from
        // `organizationProfile`, or the platform operator. The theme is left alone (see there).
        useOrganizationStore().resetToPreLogin();
    }

    // Drop only the auth token on a server 401, 1:1 Angular `UserCredentialsStore.removeAuthToken()`
    // — and note what that does NOT do: it clears the token in memory only, the persisted copy stays.
    // Production issues app tokens with a ~10y TTL, so an explicit logout is the only thing that is
    // ever meant to end a session; removing the token from localStorage here would turn any single
    // 401 (revocation, a server hiccup, a token the backend momentarily rejects) into a permanent
    // sign-out that Angular never performs — killing and reopening the app restores the session there.
    // Loaded profiles/institution stay too, so the re-auth overlay can restore the place. The overlay
    // is opened by the router guard on the next guarded navigation.
    function clearToken() {
        token.value = null;
    }

    return {
        token,
        institutionId,
        currentUser,
        familyUsers,
        loginUserTokens,
        isLoggedIn,
        isSessionValid,
        mainUserId,
        hasAtLeastOneActiveContract,
        hasMultipleAccounts,
        deviceAccountUserIds,
        setToken,
        setInstitutionId,
        setUser,
        setFamilyUsers,
        setLoginUserTokens,
        loginByToken,
        addLogoutCallback,
        logout,
        clearToken,
    };
});
